"use client";

import { Stack, Text } from "@chakra-ui/react";
import pluralize from "pluralize";
import { useMemo } from "react";

import {
  formatMeasured,
  type MeasuredTotals,
} from "@features/services/shipping/measuredTotals";
import { formatTimestamp } from "helpers/formatTimestamp";

interface MeasuredTotalsCardProps {
  measured: MeasuredTotals | null;
  /** Packages this total covers. The per-package figures live in the
   *  timeline; this card is the sum. */
  packageCount: number;
}

const MeasuredTotalsCard = ({
  measured,
  packageCount,
}: MeasuredTotalsCardProps) => {
  const summary = useMemo(
    () =>
      measured == null ? "" : formatMeasured(measured.cartons, measured.cbm),
    [measured],
  );

  const footnote = useMemo(() => {
    const parts: string[] = [];
    if (packageCount > 1) {
      parts.push(
        `Across ${packageCount} ${pluralize("package", packageCount)}`,
      );
    }
    if (measured?.at != null) {
      parts.push(
        `${parts.length === 0 ? "Measured" : "measured"} ${formatTimestamp(measured.at)}`,
      );
    }
    return parts.join(" · ");
  }, [measured, packageCount]);

  if (summary === "") {
    return null;
  }

  return (
    <Stack gap={1} bg="surface.muted" borderRadius="md" p={3}>
      <Text fontSize="xs" fontWeight="semibold" color="text.secondary">
        Received at our warehouse
      </Text>
      <Text fontSize="sm" fontWeight="medium" color="text.primary">
        {summary}
      </Text>
      {footnote !== "" && (
        <Text fontSize="xs" color="text.secondary">
          {footnote}
        </Text>
      )}
    </Stack>
  );
};

export default MeasuredTotalsCard;
